import { businessDateFor } from "./businessDate";
import { isRemote } from "@/lib/remote";
import { flushLogs, getDeviceId, isLoggingEnabled, logEvent } from "./logger";
import {
  ensureStreamId,
  markRefused,
  markSynced,
  purgeEligible,
  setMeta,
  waitingBatch,
  waitingCount,
} from "./store";

/**
 * Uploads this device's log to the server.
 *
 * Runs on its own: every 3 minutes, when the network comes back, when the app returns
 * to the foreground, at startup, at logout, and when an admin presses "Force sync".
 * The force-sync button is a nudge, not the only path — it can only reach a device
 * that is open and holding a live socket.
 *
 * What happens on each failure, because this is where logs could be lost:
 *   - server unreachable / 5xx / 401  nothing is marked; everything stays and retries
 *   - not signed in                   nothing is uploaded; the reason is recorded
 *   - server refuses a batch (422)    the batch is split until the one entry the server
 *                                     will not take is isolated; that entry is marked
 *                                     refused and KEPT, the rest upload. Without this a
 *                                     single bad entry would block every upload after
 *                                     it, forever, and the phone would fill up.
 *
 * A 401 on the current token signs the device out like any other call (sessionGuard,
 * 2026-10-30): tokens in use are renewed, so a 401 means the session is dead. The logs
 * stay queued and upload under the next sign-in.
 */
const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const SESSION_KEY = "vibez-session";
const BATCH_SIZE = 200;
const MAX_BATCHES_PER_RUN = 25;
const SYNC_INTERVAL_MS = 3 * 60 * 1000;
const STARTUP_DELAY_MS = 15 * 1000;

let inFlight = null;
let started = false;
const listeners = new Set();

export function subscribeSync(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify() {
  listeners.forEach(listener => {
    try {
      listener();
    } catch {
      /* a broken listener must not stop syncing */
    }
  });
}

export const isSyncing = () => inFlight !== null;

export function readSessionToken() {
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY) || "null")?.token || null;
  } catch {
    return null;
  }
}

/** The bundle this device is running — `main.4327fe9d` — so a stale phone is visible. */
export function bundleVersion() {
  try {
    const script = document.querySelector('script[src*="/static/js/main."]');
    const match = script?.getAttribute("src")?.match(/main\.[0-9a-f]+/);
    return match ? match[0] : "dev";
  } catch {
    return "dev";
  }
}

class UploadError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

const isPlainObject = value => value !== null && typeof value === "object" && !Array.isArray(value);

/** Exactly the fields the server's schema accepts, within its limits. */
function toWire(entry) {
  return {
    seq: entry.seq,
    occurred_at: entry.occurred_at,
    kind: String(entry.kind || "event").slice(0, 40),
    route: entry.route ? String(entry.route).slice(0, 1000) : null,
    request_id: entry.request_id ? String(entry.request_id).slice(0, 64) : null,
    detail: isPlainObject(entry.detail) ? entry.detail : { value: entry.detail ?? null },
  };
}

async function post(token, body) {
  let response;
  try {
    response = await fetch(`${API}/logs/ingest`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
  } catch (error) {
    throw new UploadError(`Server unreachable: ${error?.message || error}`, null);
  }
  if (!response.ok) {
    let detail = "";
    try {
      detail = JSON.stringify((await response.json())?.detail ?? "").slice(0, 300);
    } catch {
      /* no body */
    }
    const why =
      response.status === 401
        ? "Sign in again to upload logs"
        : `Server refused the upload (${response.status})${detail ? `: ${detail}` : ""}`;
    throw new UploadError(why, response.status);
  }
  return response.json();
}

async function uploadBatch(token, entries, base) {
  try {
    const result = await post(token, { ...base, entries: entries.map(toWire) });
    const acked = Array.isArray(result?.acked_seqs) ? result.acked_seqs : [];
    await markSynced(acked);
    return { acked: acked.length, refused: 0 };
  } catch (error) {
    if (error.status !== 422) throw error;
    if (entries.length === 1) {
      await markRefused(entries[0].seq, error.message);
      return { acked: 0, refused: 1 };
    }
    const middle = Math.ceil(entries.length / 2);
    const first = await uploadBatch(token, entries.slice(0, middle), base);
    const second = await uploadBatch(token, entries.slice(middle), base);
    return { acked: first.acked + second.acked, refused: first.refused + second.refused };
  }
}

async function record(result) {
  const at = new Date().toISOString();
  try {
    if (result.ok) {
      await setMeta("last_sync_at", at);
      await setMeta("last_sync_result", { ...result, at });
      await setMeta("last_sync_error", null);
    } else {
      await setMeta("last_sync_error", { message: result.error, status: result.status ?? null, reason: result.reason, at });
    }
  } catch {
    /* status is best effort; the entries themselves are untouched */
  }
}

async function run(reason, token) {
  const today = businessDateFor();
  try {
    await flushLogs();
    // Local housekeeping needs no server: yesterday's uploaded entries go at 3am even
    // on a device nobody has signed in to.
    let purged = await purgeEligible(today);
    if (!token) {
      await record({ ok: false, error: "Not signed in — logs are kept on this device until someone signs in.", reason });
      return { status: "skipped", reason: "no_session" };
    }
    const base = {
      device_id: getDeviceId(),
      stream_id: await ensureStreamId(),
      app_version: bundleVersion(),
      user_agent: String(navigator.userAgent || "").slice(0, 1000),
      logging_enabled: isLoggingEnabled(),
    };
    let acked = 0;
    let refused = 0;
    for (let round = 0; round < MAX_BATCHES_PER_RUN; round += 1) {
      const batch = await waitingBatch(BATCH_SIZE);
      if (!batch.length) break;
      const remaining = await waitingCount();
      const outcome = await uploadBatch(token, batch, { ...base, pending_after: Math.max(remaining - batch.length, 0) });
      acked += outcome.acked;
      refused += outcome.refused;
      // No progress means the server acknowledged nothing it was sent; stop rather
      // than loop on the same batch.
      if (outcome.acked + outcome.refused === 0) break;
    }
    purged += await purgeEligible(today);
    await record({ ok: true, acked, refused, purged, reason });
    return { status: "ok", acked, refused, purged };
  } catch (error) {
    await record({ ok: false, error: error?.message || String(error), status: error?.status, reason });
    return { status: "error", error: error?.message || String(error), httpStatus: error?.status ?? null };
  }
}

/** Upload now. Concurrent calls share one run. */
export function syncLogs(reason = "manual") {
  if (inFlight) return inFlight;
  // Read synchronously: logout clears the session right after asking for a last sync.
  const token = readSessionToken();
  inFlight = run(reason, token).finally(() => {
    inFlight = null;
    notify();
  });
  notify();
  return inFlight;
}

/** An admin asked every terminal to upload. */
export function handleServerSyncRequest(message) {
  logEvent("sync_requested_by_server", { by: message?.requested_by || null });
  return syncLogs("server_request");
}

export function startLogSync() {
  // Through RestoYaar Connect the browser is not one of the restaurant's terminals,
  // and the server refuses the upload remotely.
  if (started || typeof window === "undefined" || isRemote()) return;
  started = true;
  setInterval(() => syncLogs("interval"), SYNC_INTERVAL_MS);
  window.addEventListener("online", () => syncLogs("online"));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") syncLogs("foreground");
  });
  setTimeout(() => syncLogs("startup"), STARTUP_DELAY_MS);
}
